import { StoredPaymentMethod } from '@common'
import cx from 'classnames'
import { useTranslations } from 'next-intl'

import { useRemovePaymentMethodMutation } from '@/api'
import { ConfirmButton } from '@/components/buttons/ConfirmButton'
import { IconButton } from '@/components/buttons/IconButton'
import CollapsibleContainer from '@/components/CollapsibleContainer'
import { CreditCard, TrashBin } from '@/components/Icons'

import { PaymentMethodImage } from '../../components/Images/PaymentMethodImage'
import { RowAlert } from './RowAlert'

import styles from './PaymentInfo.module.scss'

const isExpired = (method: StoredPaymentMethod) => {
  if (!method.expiryYear || !method.expiryMonth) {
    return false
  }
  const now = new Date()
  // Javascript months start from zero
  const currentMonth = now.getMonth() + 1
  const currentYear = now.getFullYear()
  if (Number(method.expiryYear) < currentYear) {
    return true
  }
  if (Number(method.expiryYear) == currentYear && Number(method.expiryMonth) < currentMonth) {
    return true
  }
  return false
}

export const PaymentInfoContainer: React.FC<{
  storedPaymentMethods?: StoredPaymentMethod[]
  paymentMethodRemoved: () => void
}> = ({ storedPaymentMethods, paymentMethodRemoved }) => {
  const t = useTranslations()
  const [removePaymentMethod] = useRemovePaymentMethodMutation()
  if (!storedPaymentMethods || storedPaymentMethods.length === 0) {
    return (
      <CollapsibleContainer defaultOpen className={styles.ContentContainer} title={t('acc_pin_tit')}>
        <ul>
          <li className={cx(styles.PaymentMethod, styles.Empty)}>{t('acc_payment_missing')}</li>
        </ul>
      </CollapsibleContainer>
    )
  }
  return (
    <CollapsibleContainer defaultOpen className={styles.ContentContainer} title={t('acc_pin_tit')}>
      <ul>
        {storedPaymentMethods
          .filter(
            (method): method is StoredPaymentMethod & { id: string; lastFour: string } =>
              Boolean(method.id) && Boolean(method.lastFour),
          )
          .map((method) => (
            <li
              key={`${method.paymentGateway}_${method.id}`}
              className={cx(styles.PaymentMethod, { [styles.Expired]: isExpired(method) })}
            >
              <div className={styles.PaymentMethodIcon}>
                <CreditCard />
              </div>
              <div className={styles.PaymentMethodBrand}>
                <PaymentMethodImage brand={method.brand} />
              </div>
              <div className={styles.PaymentMethodNumber}>**** **** **** {method.lastFour}</div>
              <div className={styles.PaymentMethodDelete} data-cy="delete-payment-method">
                <ConfirmButton
                  confirmText={t('acc_pay_info_rem')}
                  cancelText={t('acc_pay_info_can')}
                  titleText={t('acc_rem_tit')}
                  descriptionText={t('acc_rem_bod', { lastFour: method.lastFour })}
                  onConfirm={() =>
                    removePaymentMethod({ paymentGateway: method.paymentGateway, paymentMethodId: method.id }).then(
                      () => paymentMethodRemoved(),
                    )
                  }
                  confirmButtonClass={styles.DeleteButton}
                >
                  <IconButton>
                    <TrashBin />
                  </IconButton>
                </ConfirmButton>
              </div>
              <RowAlert className={styles.PaymentMethodExpired} content={t('acc_method_expired')} />
            </li>
          ))}
      </ul>
    </CollapsibleContainer>
  )
}
